import React from "react";
import { Button, ButtonProps } from "web/react/emo/button";
import { VStack } from "web/react/emo/v-stack";
import { AuthType } from "web/redux/ducks/customer-capture-overlay";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import globals from "web/script/modules/globals";
import navigate from "web/script/utils/navigate";
import url from "web/script/utils/url";

const SSO_PROVIDERS = {
    google: "google-oauth2",
    facebook: "facebook",
    apple: "apple-id",
};

export interface SocialAuth {
    eventCategory: string;
    isLogin?: boolean;
    isRegistration: boolean;
    authType: AuthType;
    nextUrl: string;
    reason?: string;
    productId?: string | number | null;
    designerId?: string | null;
    searchQuery?: string | null;
    searchGender?: string | null;
    type: keyof typeof SSO_PROVIDERS;
}

export type SocialAuthButtonProps = SocialAuth & Omit<ButtonProps, "type">;

export default function SocialAuthButton({
    type,
    eventCategory,
    isLogin,
    isRegistration,
    nextUrl,
    reason = "",
    title,
    ...props
}: SocialAuthButtonProps): JSX.Element {
    const action = isLogin ? "click_social_sign_in" : "click_social_sign_up";
    const label = isLogin ? `signin_${type}` : `signup_${type}`;

    function onClick(): void {
        analytics.event(eventCategory, action, label);

        const parsedURL = url.parse(globals.window.location.toString());
        const next = nextUrl || url.unparse(parsedURL);

        parsedURL.path = `/social/login/${SSO_PROVIDERS[type]}/`;

        let searchParams = {};

        if (isRegistration) {
            searchParams["reason"] = reason;
            searchParams["isLogin"] = false;
        } else {
            searchParams["isLogin"] = true;
        }

        searchParams = {
            next,
            ...props,
            ...searchParams,
        };

        parsedURL.searchParams = new URLSearchParams(searchParams);
        navigate(url.unparse(parsedURL));
    }

    return (
        <Button title={title} width="full" variant={"secondary"} icon={type} onClick={onClick} />
    );
}

export function SocialAuthButtons(
    props: Omit<SocialAuthButtonProps, "type" | "title">
): JSX.Element {
    return (
        <VStack spacing="sm">
            <div>
                <SocialAuthButton
                    type={"facebook"}
                    title={
                        /* Continue with Facebook */
                        gettext("account.register.sign_up_with_provider", {
                            social_auth_provider: "Facebook",
                        })
                    }
                    {...props}
                />
            </div>
            <div>
                <SocialAuthButton
                    type="apple"
                    title={
                        /* Continue with Apple */
                        gettext("account.register.sign_up_with_provider", {
                            social_auth_provider: "Apple",
                        })
                    }
                    {...props}
                />
            </div>
            <div>
                <SocialAuthButton
                    type={"google"}
                    title={
                        /* Continue with Google */
                        gettext("account.register.sign_up_with_provider", {
                            social_auth_provider: "Google",
                        })
                    }
                    {...props}
                />
            </div>
        </VStack>
    );
}
